import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { candidateAppliedJobs } from "../../endpoints/job.service";
import { useSelector } from "react-redux";
import { colors } from "../../utils/theme";
import LoadingAnimation from "../../assets/svg/LoadingAnimation";
import dayjs from "@lib/dayjs";
import { RootState } from "@store/reducers";
import MonitorIcon from "@assets/svg/MonitorIcon";
import NotepadIcon from "@assets/svg/NotepadIcon";
import NotebookIcon from "@assets/svg/NotebookIcon";
import CheckSquareIcon from "@assets/svg/CheckSquareIcon";
// @ts-ignore
import AnimatedCircleProgress from "@components/AnimatedCircleProgress";
import { JobTypeEnum } from "@utils/constants";
import { NavLink } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

const stats = [
  {
    label: "Interviewing",
    value: 5,
    icon: <MonitorIcon />,
  },
  {
    label: "Viewed",
    value: 23,
    icon: <NotepadIcon />,
  },
  {
    label: "Submitted",
    value: 7,
    icon: <NotebookIcon />,
  },
  {
    label: "Applied",
    value: 12,
    icon: <CheckSquareIcon />,
  },
];

const JobCard = ({ data }) => {
  const navigate = useNavigate();

  const handleItemClick = () => {
    navigate("/job-track-detail", { state: { data } });
  };
  return (
    <NavLink
      to={'/job-track-detail'}
      state={{ item: data }}
      // onClick={handleItemClick}
      className="grid grid-cols-[0.6fr_2fr_0.3fr] gap-28 items-center py-6 pl-2 border-b hover:bg-gray-50"
    >
      <div className="flex flex-col gap-3.5">
        <span className="text-xs font-medium text-primary bg-gray-50 rounded-md py-2 border text-center">
          Profile Match
        </span>
        <span className="text-xs font-medium text-primary">
          Location: &nbsp;&nbsp;{data?.job?.location}
        </span>
        <span className="text-xs font-medium text-primary">
          Job Type: &nbsp;&nbsp;{JobTypeEnum[data?.job?.jobType]}
        </span>
      </div>

      <div className="flex items-center gap-8 h-full w-full">
        <div className="flex flex-col gap-3 flex-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-primary">
              {data?.job?.title}
            </span>
            <span className="text-xs font-medium text-primary">{data?.job?.refNo}</span>
          </div>
          <div className="flex flex-col border border-gray-200 bg-gray-50 rounded-md p-3 gap-2 ">
            <span className="text-xs font-medium text-primary italic">
              {dayjs(data?.latestTracker?.updatedAt).format("hh:mm a DD MMM YY")}
            </span>
            <span className="text-xs font-medium text-primary">
              Latest Activity : {data?.latestTracker?.status || 'N/A'}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2 h-full justify-end pb-2 w-32">
          <span className="text-xs font-medium text-light-black">Agency</span>
          <span className="text-xs font-medium text-primary">{data?.job?.user?.companyName || 'N/A'}</span>
        </div>
      </div>
      <div className="flex flex-col h-full justify-end gap-3">
        <span className="text-xs font-medium text-primary">Matched %</span>
        <div className="relative">
          <AnimatedCircleProgress progress={data?.matchingPercentage} />
        </div>
      </div>
    </NavLink>
  );
};

const LIMIT = 25

const JobTrackPage = () => {
  const navigate = useNavigate();
  const sentinelRef = useRef(null)
  const scrollListRef = useRef(null);

  const { user } = useSelector((state: RootState) => state.auth);

  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMoreData, setHasMoreData] = useState(false)
  const [activeTab, setActiveTab] = useState("Applied");
  const [jobList, setJobList] = useState([]);
  const hasFetched = useRef(false);

  const filteredJobs = jobList
    ?.filter((job) => job?.latestTracker?.status === activeTab)
    // @ts-ignore
    ?.sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt));

  const statusCounts = {};
  jobList?.forEach((job) => {
    const status = job?.latestTracker?.status;
    if (status) {
      if (!statusCounts[status]) {
        statusCounts[status] = 1;
      } else {
        statusCounts[status]++;
      }
    }
  });

  const fetchAppliedJobs = useCallback(async () => {
    try {
      if (page > 1) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true)
      }
      const { data, success } = await candidateAppliedJobs({
        page,
        limit: LIMIT
      });
      if (success) {
        setJobList((prev) => {
          return page === 1 ?
            data?.jobApplications
            :
            [...prev, ...data?.jobApplications]
        });
        setHasMoreData(data?.pagination?.hasMore)
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: 'error' })
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false)
    }
  }, [page, hasMoreData]);

  function handleLoadMore() {
    if (!hasMoreData || isLoadingMore) return
    setPage((prev) => prev + 1)
  }

  useEffect(() => {
    // if (!hasFetched.current) {
    //   hasFetched.current = true;
    // }
    fetchAppliedJobs();
  }, [page]);

  useEffect(() => {
    if (!sentinelRef?.current || !scrollListRef?.current) return
    const observer = new IntersectionObserver((entries) => {
      if (entries?.[0]?.isIntersecting) {
        handleLoadMore()
      }
    }, {
      root: scrollListRef?.current,
      rootMargin: '0px',
      threshold: 1
    })
    observer.observe(sentinelRef?.current)
    return () => observer.disconnect()
  }, [isLoading, isLoadingMore])

  console.log("filteredJobs", filteredJobs);

  return (
    <div className="flex flex-col rounded-lg bg-white w-full flex-grow min-h-0">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <LoadingAnimation color={colors.PrimaryBlack} className={"w-6 h-6"} />
        </div>
      ) : !jobList?.length ? (
        <div className="flex flex-1 justify-center items-center">
          <p className="text-sm text-center font-medium text-light-black">
            You haven't applied in any jobs yet.
          </p>
        </div>
      ) : (
        <>
          {/* List Section */}
          <div
            ref={scrollListRef}
            className="flex flex-col min-h-0 overflow-y-scroll no-scrollbar flex-1">
            {jobList?.map((jobApplication) => {
              return (
                <JobCard key={jobApplication?._id} data={jobApplication} />
              );
            })}
            {/* Sentinel div */}
            <div ref={sentinelRef} className="h-px"></div>
          </div>
          {
            isLoadingMore ?
              <div className="flex items-center gap-2 font-medium italic text-xs mx-auto mt-3">
                <LoadingAnimation color="black" />
                Loding more data...
              </div>
              : null
          }
        </>
      )}
    </div>
  );
};

export default JobTrackPage;

// import { useCallback, useEffect, useRef, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { candidateAppliedJobs } from "../../endpoints/job.service";
// import { useSelector } from "react-redux";
// import { colors } from "../../utils/theme";
// import LoadingAnimation from "../../assets/svg/LoadingAnimation";
// import dayjs from "@lib/dayjs";
// import { RootState } from "@store/reducers";
// import StackIcon from "@assets/svg/StackIcon";
// import MonitorIcon from "@assets/svg/MonitorIcon";
// import NotepadIcon from "@assets/svg/NotepadIcon";
// import NotebookIcon from "@assets/svg/NotebookIcon";
// import CheckSquareIcon from "@assets/svg/CheckSquareIcon";
// // @ts-ignore
// import AgencyImg from "@assets/images/agency.jpg";

// const stats = [
//   {
//     label: "Interviewing",
//     value: 5,
//     icon: <MonitorIcon />,
//   },
//   {
//     label: "Viewed",
//     value: 23,
//     icon: <NotepadIcon />,
//   },
//   {
//     label: "Submitted",
//     value: 7,
//     icon: <NotebookIcon />,
//   },
//   {
//     label: "Applied",
//     value: 12,
//     icon: <CheckSquareIcon />,
//   },
// ];

// const JobTrackPage = () => {
//   const navigate = useNavigate();
//   const { user } = useSelector((state: RootState) => state.auth);
//   const [isLoading, setIsLoading] = useState(false);
//   const [activeTab, setActiveTab] = useState("Applied");
//   const [jobList, setJobList] = useState([]);
//   const hasFetched = useRef(false);

//   // const filteredJobs = jobList?.filter(
//   //   (job) => job?.latestTracker?.status === activeTab
//   // );

//   const filteredJobs = jobList
//     ?.filter((job) => job?.latestTracker?.status === activeTab)
//     // @ts-ignore
//     ?.sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt));

//   const statusCounts = {};
//   jobList?.forEach((job) => {
//     const status = job?.latestTracker?.status;
//     if (status) {
//       if (!statusCounts[status]) {
//         statusCounts[status] = 1;
//       } else {
//         statusCounts[status]++;
//       }
//     }
//   });

//   useEffect(() => {
//     if (!hasFetched.current) {
//       hasFetched.current = true;
//       fetchAppliedJobs();
//     }
//   }, [user]);

//   const fetchAppliedJobs = useCallback(async () => {
//     try {
//       setIsLoading(true);
//       const { data, success } = await candidateAppliedJobs();
//       if (success) {
//         setJobList(data?.jobApplications);
//       }
//     } catch (error) {
//       console.error(error);
//     } finally {
//       setIsLoading(false);
//     }
//   }, []);

//   const handleItemClick = (item) => {
//     navigate("job-track-detail", { state: { item } });
//   };

//   console.log("filteredJobs", filteredJobs);

//   return (
//     <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-6">
//       {/* Top container */}
//       <div className="flex items-center justify-between mb-10">
//         {/* Col 1 */}
//         <div className="w-[38%] flex flex-col gap-4">
//           <div className="bg-light-white-700 w-full flex items-center gap-2 px-3 py-2 rounded-md">
//             <div className="rounded-md flex bg-white w-7 h-7 items-center justify-center">
//               <StackIcon className="w-5 h-5" />
//             </div>
//             <span className="font-semibold text-primary text-xs">
//               Review your job matches
//             </span>
//           </div>
//           {/* Search section */}
//           <div className="flex items-center gap-4">
//             <div className="bg-light-white-700 rounded-md p-1.5 flex-1">
//               <input
//                 className="bg-white rounded-md text-xs h-8 px-3 font-medium w-full outline-none"
//                 placeholder="Search"
//               />
//             </div>
//             <button className="bg-light-white-700 h-11 px-5 text-xs rounded-md flex items-center justify-center font-semibold text-primary hover:bg-gray-200">
//               Filter
//             </button>
//           </div>
//         </div>
//         {/* Col 2 */}
//         <div className="w-[38%] grid grid-cols-2 gap-2.5">
//           {stats?.map((stat) => {
//             return (
//               <div
//                 key={stat?.label}
//                 className="flex items-center gap-2 bg-light-white-700 rounded-md p-3"
//               >
//                 <div className="flex items-center justify-center bg-white rounded-md h-7 w-7">
//                   {stat?.icon}
//                 </div>
//                 <span className="font-semibold text-xs ">
//                   {stat?.label} :&nbsp; {stat?.value}
//                 </span>
//               </div>
//             );
//           })}
//         </div>
//       </div>
//       {isLoading ? (
//         <div className="flex items-center justify-center h-full">
//           <LoadingAnimation color={colors.PrimaryBlack} className={"w-4 h-4"} />
//         </div>
//       ) : jobList === undefined ? (
//         <p className="text-sm text-center mt-20">
//           You haven't applied in any jobs yet.
//         </p>
//       ) : (
//         <>
//           <div className="grid grid-cols-[0.8fr_1.5fr_0.8fr] gap-10 border-b pb-3 px-5">
//             <span className="font-medium text-xs text-primary">
//               Job post title
//             </span>
//             <span className="font-medium text-xs text-primary">
//               Status Tracker
//             </span>
//             <span className="font-medium text-xs text-primary">Recruiter</span>
//           </div>
//           {/* List View */}
//           <div className="flex flex-col min-h-0 overflow-y-scroll no-scrollbar">
//             {jobList?.map((jobApplication) => {
//               return (
//                 <div
//                   key={jobApplication?._id}
//                   onClick={() => handleItemClick(jobApplication)}
//                   className="grid grid-cols-[0.8fr_1.5fr_0.8fr] gap-10 items-center px-5 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
//                 >
//                   <div className="flex flex-col gap-2.5">
//                     <span className="text-xs font-bold">
//                       {jobApplication?.job?.title}
//                     </span>
//                     <span className="text-xs text-light-black">
//                       {jobApplication?.job?.refNo}
//                     </span>
//                   </div>
//                   <div className="flex flex-col gap-2">
//                     <span className="text-xs italic text-light-black">
//                       {dayjs(jobApplication?.appliedAt).format("h:mm a DD MMM")}
//                     </span>
//                     <div className="flex items-center gap-3 bg-[#FAFAFA] rounded-md p-1.5">
//                       <img
//                         src={AgencyImg}
//                         className="w-9 h-9 rounded-full border"
//                       />
//                       <span className="text-xs text-light-black">
//                         <span className="font-semibold text-primary">
//                           ABC Recruitment
//                         </span>{" "}
//                         has submitted your application to Google INC for the
//                         position titled{" "}
//                         <span className="font-semibold text-primary">
//                           React front end developer
//                         </span>
//                       </span>
//                     </div>
//                   </div>
//                   <div className="flex items-center gap-3">
//                     <img src={AgencyImg} className="w-9 h-9 rounded-full" />
//                     <div className="flex flex-col gap-1">
//                       <span className="text-xs text-primary font-semibold">
//                         ABC Recruitment
//                       </span>
//                       <span className="text-xs text-light-black">RA-123BN</span>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//           {/* <div className="flex border-b gap-10 text-sm font-medium text-gray-700 pl-6">
//             <p className="text-xs flex font-semibold text-primary items-center">
//               Applications Status :
//             </p>

//             <button
//               className={`text-xs px-4 py-2 mb-1 ${
//                 activeTab === "Applied"
//                   ? "font-medium  border rounded-sm bg-[#F2F2F2] border-[#DDDDDD] text-primary"
//                   : "text-gray-500 border border-transparent"
//               }`}
//               onClick={() => setActiveTab("Applied")}
//             >
//               Applied {statusCounts["Applied"] || 0}{" "}
//               <span className="text-[#00AAFF]">(2)</span>
//             </button>
//             <button
//               className={`text-xs px-4 py-2 mb-1 ${
//                 activeTab === "Application Viewed"
//                   ? "font-medium border rounded-sm  bg-[#F2F2F2] border-[#DDDDDD] text-primary"
//                   : "text-gray-500 border border-transparent"
//               }`}
//               onClick={() => setActiveTab("Application Viewed")}
//             >
//               Viewed {statusCounts["Application Viewed"] || 0}{" "}
//               <span className="text-[#00AAFF]">(2)</span>
//             </button>
//             <button
//               className={`text-xs px-4 py-2 mb-1 ${
//                 activeTab === "Interview Scheduled"
//                   ? "font-medium  border  bg-[#F2F2F2]  border-[#DDDDDD] text-primary"
//                   : "text-gray-500 border border-transparent"
//               }`}
//               onClick={() => setActiveTab("Interview Scheduled")}
//             >
//               Interviewing {statusCounts["Interview Scheduled"] || 0}{" "}
//               <span className="text-[#00AAFF]">(2)</span>
//             </button>
//             <button
//               className={`text-xs px-4 py-2 mb-1 ${
//                 activeTab === "Job Close"
//                   ? "font-medium  border rounded-sm bg-[#F2F2F2] border-[#DDDDDD] text-primary"
//                   : "text-gray-500 border border-transparent"
//               }`}
//               onClick={() => setActiveTab("Job Close")}
//             >
//               Closed jobs {statusCounts["Job Close"] || 0}{" "}
//               <span className="text-[#00AAFF]">(2)</span>
//             </button>

//             <button
//               className={`text-xs px-4 py-2 mb-1 ${
//                 activeTab === "Candidate Placed"
//                   ? "font-medium  border rounded-sm bg-[#F2F2F2] border-[#DDDDDD] text-primary"
//                   : "text-gray-500 border border-transparent"
//               }`}
//               onClick={() => setActiveTab("Candidate Placed")}
//             >
//               Successful placements {statusCounts["Candidate Placed"] || 0}{" "}
//               <span className="text-[#00AAFF]">(2)</span>
//             </button>
//           </div> */}
//           {/*
//           <div className="grid grid-cols-[2fr,1fr,2fr,1fr,1fr] gap-2 px-6 py-4 bg-[#FAFAFA] text-xs font-semibold text-[#6F6F6F]  border-gray-200 rounded-lg mt-8">
//             <div>Job post title</div>
//             <div className="px-8">Status</div>
//             <div>Status change time</div>
//             <div>Recruiter</div>
//             <div className="text-center">Reference</div>
//           </div>

//           <div className="overflow-y-scroll no-scrollbar mt-1">
//             <div className="overflow-y-scroll no-scrollbar">
//               {filteredJobs?.length > 0 ? (
//                 filteredJobs.map((data, index) => (
//                   <div
//                     key={index}
//                     className="grid grid-cols-[2fr,1fr,2fr,1fr,1fr]  gap-4 items-center px-6 py-6 text-xs  border-b text-gray-700 radius-[4px] border-gray-200 hover:bg-gray-50 cursor-pointer"
//                     onClick={() => handleItemClick(data)}
//                   >
//                     <div className="font-bold text-gray-800">
//                       {data?.job?.title ?? "N/A"}
//                     </div>
//                     <div className="text-gray-600 font-medium px-8">
//                       {data?.latestTracker?.status}
//                     </div>
//                     <div className="text-gray-500 italic">
//                       {dayjs(data?.appliedAt).format("h:mm a DD MMMM YYYY")}
//                     </div>
//                     <div className="text-[#6F6F6F]">
//                       {data?.job?.companyDetails?.name ?? "N/A"}
//                     </div>
//                     <div className="text-[#6F6F6F] font-normal text-center">
//                       {data?.job?.refNo ?? "N/A"}
//                     </div>
//                   </div>
//                 ))
//               ) : (
//                 <p className="text-sm text-center text-gray-500 mt-20">
//                   No jobs found under{" "}
//                   <span className="font-semibold">{activeTab}</span> tab.
//                 </p>
//               )}
//             </div>
//           </div> */}
//         </>
//       )}
//     </div>
//   );
// };

// export default JobTrackPage;
